import React, { useEffect } from 'react';
import { useLatest } from 'ahooks';
import useCOS from '@gp/hooks/useCos/useCOS';
import useReportDeletedCheck from './useReportDeletedCheck';
import { fetchReportTencentQCouldMassage } from '@intelligence/services/cos';
import { getFileData } from '../utils';
import { ENV } from '@home/_config/config';
import { COS_USE_ACCELERATE_ENVS } from '@intelligence/_config/config';

import type { AxiosResponse } from 'axios';

const fetchReportGZTencentQCouldMassage = async () => {
  const res = await fetchReportTencentQCouldMassage({ cos_config: 'guangzhou' });
  return res?.data;
};

const DEFAULT_COS_OPTIONS: IUseCOSOptions = {
  // 仅预发布、正式 支持COS全球域名加速请求
  useAccelerate: false,
  baseCOSConfigFetcher: fetchReportGZTencentQCouldMassage,
  authorizationFetcher: fetchReportGZTencentQCouldMassage,
};

export default function useCOSDocPreview(props: {
  // 是否可执行该hooks内逻辑，仅COS预览时执行该hooks内逻辑
  executable: boolean;
  cosOptions?: IUseCOSOptions;
  cosDocKey?: string;
  systemId: string;
  studioId?: string;
  resourceId?: string;
  watermarkEnable?: string;
  fetchPDFUrl: () => AxiosResponse<Blob>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setFileSuffix: React.Dispatch<React.SetStateAction<string>>;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setPreviewUrl: React.Dispatch<React.SetStateAction<string>>;
  // 加载成功回调
  onSuccess?: (payload: { url: string; previewUrl: string }) => void;
  // 加载失败回调
  // REMARK: 这里可以扩展加载异常的处理逻辑
  onError?: (exception: unknown) => void;
  // 报告被删除回调
  onDeleted?: () => void;
}) {
  const {
    executable = false,
    cosOptions = DEFAULT_COS_OPTIONS,
    cosDocKey,
    systemId,
    studioId = '',
    resourceId = '',
    watermarkEnable = '',
    fetchPDFUrl,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onSuccess,
    onError,
    onDeleted,
  } = props;

  const fetchPDFUrlLatest = useLatest(fetchPDFUrl);
  const { checkReportDeleted } = useReportDeletedCheck({ executable, systemId, studioId, resourceId });

  const { isCOSInited, isObjectExist, getDocumentPreviewUrl } = useCOS({
    ...cosOptions,
    // 仅预发布、正式 支持COS全球域名加速请求
    useAccelerate: COS_USE_ACCELERATE_ENVS.includes(ENV),
  });

  // 检查文档是否存在
  const checkExist = async (key: string): Promise<boolean> => {
    if (!executable) return false;
    return await isObjectExist({ key });
  };

  const updateDownloadUrl = async (): Promise<{ isError: boolean; url: string; suffix: string }> => {
    if (!executable) return { isError: false, url: '', suffix: '' };
    let isError = false;
    let url = '';
    let suffix = '';

    try {
      const res = await fetchPDFUrlLatest.current?.();
      const fileData = getFileData(res);
      url = fileData?.url || '';
      suffix = fileData?.suffix || '';
    } catch (error) {
      console.error(error);
      isError = true;
      url = '';
      suffix = '';
    }

    setFileSuffix(suffix);
    setUrl(url);
    return { isError, url, suffix };
  };

  const updateUrl = async (key: string) => {
    if (!executable) return;
    // 前置检测报告是否被删除
    const isDeleted = await checkReportDeleted();
    if (isDeleted) {
      onDeleted?.();
      return;
    }
    try {
      setIsLoading(true);
      const exist = await checkExist(key);
      if (!exist) throw new Error('The document has been deleted by the user.');
      let userName = sessionStorage.getItem('userName') || '';
      userName = userName.replace(/^"|"$/g, '');
      userName = !userName ? '' : `${userName}@databrain`;
      const { url } = await getDocumentPreviewUrl(
        { key },
        {
          htmlhorizontal: '200',
          htmlvertical: '300',
          htmlfillstyle: 'rgba(106, 109, 126, 0.4)',
          // htmlwaterword: userName ? `${userName}@databrain` : '',
          useHtmlwatermark: watermarkEnable !== '0',
          htmlwaterword: userName,
        },
      );
      setPreviewUrl(url || '');
      setIsError(false);
      setIsLoading(false);
      onSuccess?.({
        url: '',
        previewUrl: url || '',
      });
    } catch (exception: unknown) {
      let isError = true;
      try {
        const decoder = new TextDecoder('utf-8');
        const exceptionJSONString = decoder.decode(exception as any);
        const a = JSON.parse(exceptionJSONString);
        if (a.code === 4001) {
          isError = false;
        }
      } catch (e) {
        console.error(e);
      }
      if (isError) {
        setIsError(true);
        setIsLoading(false);
        onError?.(exception);
      }
    }
  };

  const handleReload = (e: React.SyntheticEvent<HTMLElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!executable) return;
    updateUrl(cosDocKey || '');
  };

  useEffect(() => {
    if (!executable) return;
    if (!isCOSInited) return;

    updateUrl(cosDocKey || '');
  }, [executable, isCOSInited, cosDocKey]);

  return { updateDownloadUrl, onReload: handleReload };
}
